import React, { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';

// Components
import OffersContainer from '../components/offers/OffersContainer';
import SelectedOffersContainer from '../components/selected-offers/SelectedOffersContainer';

// Redux
import { useDispatch, useSelector } from 'react-redux';

// Types
import { RootState } from '../store/redux/reducers/rootReducer';
import { OfferUris } from '../../../../types/ProductFactory';
import { getAllInsuranceCompanies } from '../../../common/src/legacy/services/apiRouterService';
import cogoToast from 'cogo-toast';
import { formatErrorMessage } from '../../../common/src/legacy/services/errorMessageService';
import {
  addToSelectedOffers,
  removeFromSelectedOffers,
  setInsuranceCompanies,
  setCachedSessionId,
  addVisitedOverviewPage,
  setTerminationLetterModal,
  setActiveCarQuoteFilter,
} from '../store/redux/slices/offersSlice';
import { InsuranceCompaniesResp } from '../../../../types/Broker';
import styled from 'styled-components';
import { mq } from '../../../common/src/utils/styledComponentsUtils';
import { loadSelectionState } from '../store/redux/slices/selectionSlice';
import { useQueryClient } from 'react-query';
import { useLouiseDispatch } from '../store/redux/store';
import { getTopProductsBySessionId } from '../services/louiseApiService';
import { OFFERS_CACHE_TIME, onProductSuccess } from '../hooks/useQuoteQueries';
import LoadingSpinner from '../../../common/src/components/LoadingSpinner';
import {
  ProductErrorResponse,
  ProductsQualityRespWithPagination,
} from '../../../../types/Quotes';
import useSessionId from '../../../flows/src/hooks/useSessionId';
import ContentLayout from '../components/layout/ContentLayout';
import useTracking from '../../../common/src/hooks/tracking/useTracking';
import AddToOffersModal from '../components/Modal/AddToOffersModal';

interface Props {
  offersUriArray?: OfferUris;
}

// For anyone working on this, NEVER hardcode the risk objects for the offer flow.
// Always use this as a reference to the selected risk objects.
// This will ensure future-proofness for when new type of risk objects pop up
const OffersView: React.FC<React.PropsWithChildren<Props>> = () => {
  const dispatch = useDispatch();
  const appDispatch = useLouiseDispatch();
  const history = useHistory();
  const { trackEvent } = useTracking();
  const location = useLocation();
  const { page, riskObject } = useParams<{
    sessionId: string;
    page: string;
    riskObject: string;
  }>();
  const sessionId = useSessionId();

  const [loading, setLoading] = useState(true);
  const [selectedQuoteId, setSelectedQuoteId] = useState<string>();

  const selectedRiskObjects = useSelector(
    (state: RootState) => state.selection.selectedRiskObjects,
  );
  const selectedOffers = useSelector(
    (state: RootState) => state.offers.selectedOffers,
  );
  const offersInfo = useSelector((state: RootState) => state.offers.offersInfo);
  const visitedOverviewPages = useSelector(
    (state: RootState) => state.offers.visitedOverviewPages,
  );
  const cachedSessionId = useSelector(
    (state: RootState) => state.offers.cachedSessionId,
  );
  const comparisonMatrixOffers = useSelector(
    (state: RootState) => state.offers.comparisonMatrixOffers,
  );
  const terminationLetterModalType = useSelector(
    (state: RootState) => state.offers.terminationLetterModalType,
  );

  const queryClient = useQueryClient();

  useEffect(() => {
    dispatch(setActiveCarQuoteFilter(''));
  }, []);

  useEffect(() => {
    initial();
    storeSessionId();
  }, [sessionId]);

  useEffect(() => {
    return () => {
      if (page === 'overview' && riskObject && visitedOverviewPages) {
        dispatch(addVisitedOverviewPage(riskObject));
      }
    };
  }, [riskObject]);

  useEffect(() => {
    if (selectedRiskObjects.length > 0 && riskObject) prefetchProducts();
  }, [selectedRiskObjects, riskObject]);

  const handleOnBack = () => {
    history.push({});
  };

  const getAllAvailableInsuranceCompanies = async () => {
    const [resp, status] = await getAllInsuranceCompanies();

    if (status !== 200) {
      const { detail } = formatErrorMessage('', resp?.detail);
      return cogoToast.error(detail);
    }

    const mappedInsuranceCompanies = (resp as InsuranceCompaniesResp).items.map(
      ({ insurance_company }) => insurance_company,
    );

    dispatch(setInsuranceCompanies(mappedInsuranceCompanies));
  };

  const storeSessionId = () => {
    // When current session id is different from cached session id it should reset the offer store
    if (!cachedSessionId) {
      dispatch(setCachedSessionId(sessionId));
    } else if (cachedSessionId !== sessionId) {
      dispatch(setCachedSessionId(sessionId));
    }
  };

  /**
   * Do initial stuff before offers can be shown
   */
  const initial = async () => {
    await getAllAvailableInsuranceCompanies();
    dispatch(setTerminationLetterModal(''));
    await appDispatch(loadSelectionState(sessionId));
  };

  useEffect(() => {
    if (selectedRiskObjects && selectedRiskObjects?.length > 0) {
      if (!page)
        history.replace(
          location.pathname +
            `/overview/${
              [...selectedRiskObjects].sort()[0]
            }?session_id=${sessionId}`,
        );

      setLoading(false);
    }
  }, [selectedRiskObjects]);

  const handleAddToSelectedOffers = (id?: string, showModal = true) => {
    if (!id) return;
    if (
      showModal &&
      !offersInfo.insuranceTerminationInfo[riskObject] &&
      riskObject !== 'funeral'
    ) {
      dispatch(setTerminationLetterModal(riskObject));
      setSelectedQuoteId(id);
    } else {
      trackEvent('Add offer to basket in Sales Conversation');
      dispatch(addToSelectedOffers({ riskObject, id }));
    }
  };

  const handleRemoveFromSelectedOffers = (id?: string) => {
    if (!id) return;
    if (selectedOffers[riskObject]?.includes(id))
      dispatch(removeFromSelectedOffers({ riskObject, id }));
  };

  const prefetchProducts = async () => {
    await Promise.all(
      selectedRiskObjects
        .filter((_riskObject) => _riskObject !== riskObject)
        .map(async (riskObject) => {
          try {
            const resp = await queryClient.fetchQuery(
              [sessionId, 'products', riskObject],
              async () => {
                const [resp] = await getTopProductsBySessionId(
                  sessionId,
                  riskObject,
                );
                if (resp.code) {
                  return Promise.reject(resp as ProductErrorResponse);
                }
                return Promise.resolve(
                  resp as ProductsQualityRespWithPagination,
                );
              },
              {
                staleTime: Infinity,
                cacheTime: OFFERS_CACHE_TIME,
              },
            );
            return onProductSuccess(resp, dispatch);
          } catch (e) {
            // Keep this for debugging
            console.error(e);
          }
        }),
    );
  };

  return (
    <>
      {terminationLetterModalType && (
        <AddToOffersModal
          riskObject={terminationLetterModalType}
          quoteId={selectedQuoteId}
          showModal={!!terminationLetterModalType}
          onClose={() => dispatch(setTerminationLetterModal(''))}
          addToSelectedOffers={(showModal) =>
            handleAddToSelectedOffers(selectedQuoteId, showModal)
          }
        />
      )}

      <StyledContentLayout onBack={handleOnBack}>
        {{
          overview: (
            <>
              <OffersContainer
                riskObject={riskObject}
                comparisonQuotesAmount={
                  comparisonMatrixOffers[riskObject]?.length
                }
                addToSelectedOffers={handleAddToSelectedOffers}
                removeFromSelectedOffers={handleRemoveFromSelectedOffers}
                isProductsLoading={loading}
              />
            </>
          ),
          selectedOffers: (
            <SelectedOffersContainer isProductsLoading={loading} />
          ),
        }[page] || <LoadingSpinner />}
      </StyledContentLayout>
    </>
  );
};

const StyledContentLayout = styled(ContentLayout)`
  /**
  * The 8 rem stands for the height of the actioncontainer
  */
  max-height: calc(100% - 8rem);
  background-color: white;
  overflow: hidden;
  grid-template-rows:
    minmax(1px, 1fr)
    auto;

  ${mq.laptop} {
    max-height: unset;
  }
`;

export default OffersView;
